export default {
  // Navigation and menu
  nav: {
    sessions: 'Sessions',
    admin: 'Admin Panel',
    prompts: 'Prompts',
    logout: 'Logout',
    settings: 'Settings',
    changelog: 'Changelog',
    downloadPlugin: 'Download Plugin',
    tipsAndTricks: 'Tips and Tricks',
    privacyPolicy: 'Privacy Policy'
  },

  // Authentication
  auth: {
    title: 'AI Stenograph',
    subtitle: 'Meeting Transcription and Summarization',
    email: 'Email',
    password: 'Password',
    login: 'Login',
    loginWithGoogle: 'Login with Google',
    loading: 'Loading...',
    loggingIn: 'Logging in...',
    authError: 'Authentication Error',
    invalidCredentials: 'Invalid email or password',
    serverError: 'Server error. Please try again later.',
    welcome: 'Welcome',
    checkingAuth: 'Checking authorization...',
    checkingPermissions: 'Checking permissions...',
    loginSuccess: 'Authorization successful! Loading dashboard...',
    accessDenied: 'You do not have access to the system',
    permissionCheckError: 'Error checking permissions',
    connectionError: 'Server connection error',
    loggedOut: 'You have logged out',
    checkingSavedAuth: 'Checking saved authorization...',
    reAuthRequired: 'Re-authorization required'
  },

  // User
  user: {
    roles: {
      owner: 'Owner',
      administrator: 'Administrator',
      user: 'User'
    },
    tokenValid: 'Token valid:',
    tokenExpired: 'Token expired',
    defaultName: 'User'
  },

  // Sessions
  sessions: {
    title: 'My Recordings',
    search: 'Search by title or transcription...',
    noSessions: 'You have no recordings yet',
    noSessionsDesc: 'To get started, you need to install the Chrome plugin and log in. After that, you can record and transcribe audio from browser tabs and microphone.',
    installPlugin: 'Install Plugin',
    pluginLoginHint: 'After installation, log in to the plugin to sync your recordings',
    features: {
      recording: 'Record from tabs and microphone',
      transcription: 'AI-powered transcription',
      summary: 'Automatic summary generation'
    },
    loading: 'Loading recordings...',
    status: {
      processing: 'Processing',
      completed: 'Completed',
      failed: 'Error',
      finalizing: 'Finalizing',
      pending: 'Pending',
      receiving: 'Receiving',
      paused: 'Paused',
      recording: 'Recording',
      reprocessing: 'Reprocessing'
    },
    duration: 'Duration',
    view: 'Open',
    viewText: 'Text',
    viewInterim: 'View interim text',
    viewTranscript: 'View transcription text',
    delete: 'Delete',
    deleteSession: 'Delete session',
    deleteConfirm: 'Delete recording?',
    deleteWarning: 'This action cannot be undone',
    deleteEntry: 'Delete Recording',
    cancel: 'Cancel',
    summary: 'Summary',
    createSummary: 'Create',
    viewSummary: 'View summary',
    transcription: 'Transcription',
    transcriptionText: 'Transcription text',
    noTranscription: 'Transcription unavailable',
    noSummary: 'Summary not created yet',
    noSummaryDesc: 'Click the "➕ Create" button above to generate a brief summary of the transcription.',
    viewSummary: 'View summary',
    createSummary: 'Create summary',
    recreateSummary: 'Recreate summary',
    recreate: 'Recreate',
    promptLabel: 'Prompt:',
    fullIdTooltip: 'Full ID: {id} (click to copy)',
    dateUnknown: 'Date unknown',
    copyTranscript: 'Copy transcription',
    copySummary: 'Copy summary',
    copied: 'Copied!',
    download: 'Download',
    downloaded: 'Downloaded',
    regenerateSummary: 'Regenerate summary',
    regenerating: 'Regenerating...',
    generatingSummary: 'Generating summary...',
    close: 'Close',
    edit: 'Edit',
    save: 'Save',
    editTitle: 'Edit title',
    saveTitle: 'Save title',
    titleSaved: 'Title saved',
    titleError: 'Error saving title',
    anonymousUser: 'Anonymous user',
    fullId: 'Full ID',
    clickToCopy: 'click to copy',
    ttlInfinite: 'TTL infinite',
    makeTTLInfinite: 'Click to make TTL infinite',
    ttlExpired: 'Expired',
    systemStats: 'System Statistics',
    totalSessions: 'Total sessions',
    completedSessions: 'Completed',
    failedSessions: 'Failed',
    processingTime: 'Average processing time',
    createNewSummary: 'Create new summary',
    selectSavedPrompt: 'Select from saved prompts:',
    orEnterCustom: 'or enter your own prompt',
    enterCustomPrompt: 'Enter prompt for new summary...',
    enterPromptPlaceholder: 'Enter prompt for new summary...',
    generate: 'Generate',
    screenshots: 'Screenshots',
    autoScreenshots: 'Automatically save screen.',
    scissors: 'Scissors',
    waitingForTranscription: '[Waiting for first transcription...]',
    transcriptionWillAppear: 'Text will appear approximately 60 seconds after recording starts.',
    autoUpdating: 'auto-updating',
    streamingMode: 'Streaming processing (real-time)',
    qualityMode: 'Quality processing (post-recording)',
    // Audio
    hasAudio: 'Audio saved',
    audioNotSaved: 'Audio not saved',
    audioSkippedStorageLimit: 'Storage limit exceeded',
    audioAutoDeleted: 'Audio auto-deleted',
    audioLocked: 'Audio protected from deletion',
    audioUnlocked: 'Audio not protected',
    audio: 'Audio',
    lockAudio: 'Protect from auto-deletion',
    unlockAudio: 'Remove protection',
    playAudio: 'Play audio',
    loadingAudio: 'Loading audio...',
    downloadAudio: 'Download audio',
    deleteAudio: 'Delete audio',
    deleteAudioConfirm: 'Delete audio recording? Transcription will be kept.',
    reprocess: 'Reprocess',
    reprocessAgain: 'Reprocess again',
    reprocessConfirm: 'Reprocess in quality mode? Current transcription will be replaced.',
    reprocessing: 'Reprocessing...',
    reprocessStarted: 'Reprocessing started',
    reprocessCompleted: 'Reprocessing completed',
    audioDeleted: 'Audio recording deleted',
    audioDownloadError: 'Error downloading audio'
  },

  // Admin panel
  admin: {
    title: 'Users',
    subtitle: 'User access management',
    search: 'Search by email...',
    addUser: 'Add User',
    noUsers: 'No users found',
    loading: 'Loading users...',
    email: 'Email',
    role: 'Role',
    created: 'Created',
    actions: 'Actions',
    edit: 'Edit',
    delete: 'Delete',
    roles: {
      owner: 'owner',
      administrator: 'administrator',
      user: 'user'
    },
    addUserTitle: 'Add New User',
    editUserTitle: 'Edit User',
    password: 'Password',
    betaTester: 'Beta Tester',
    betaTesterAdded: 'User added to beta testers',
    betaTesterRemoved: 'User removed from beta testers',
    betaTesterError: 'Error changing beta tester status',
    qualitySettings: 'Processing Settings',
    qualityMode: 'Quality Processing',
    storageLimit: 'Storage Limit',
    deleteUser: 'Delete user',
    passwordHint: 'Leave empty to keep current',
    save: 'Save',
    saving: 'Saving...',
    cancel: 'Cancel',
    deleteConfirm: 'Delete user?',
    deleteWarning: 'This action cannot be undone',
    userAdded: 'User added',
    userUpdated: 'User updated',
    userDeleted: 'User deleted',
    error: 'Operation error',
    loadError: 'Error loading users list',
    addError: 'Error adding user',
    deleteUserTitle: 'Delete User',
    roleChangeError: 'Error changing role',
    changesSaved: 'Changes successfully saved',
    changeError: 'Error making changes'
  },

  // Prompts
  prompts: {
    title: 'My Prompts',
    subtitle: 'Manage prompt templates for summaries',
    create: 'Create Prompt',
    created: 'Created:',
    updated: 'Updated:',
    createdAt: 'Created',
    updatedAt: 'Updated',
    noPrompts: 'You have no saved prompts yet',
    createFirst: 'Create first prompt',
    loading: 'Loading prompts...',
    newPrompt: 'New Prompt',
    editPrompt: 'Edit Prompt',
    name: 'Name',
    namePlaceholder: 'e.g.: Brief summary',
    content: 'Prompt text',
    contentPlaceholder: 'Enter prompt text...',
    save: 'Save',
    saving: 'Saving...',
    cancel: 'Cancel',
    edit: 'Edit',
    delete: 'Delete',
    confirmDeleteTitle: 'Confirm Action',
    confirmDeleteMessage: 'Are you sure you want to delete this prompt?',
    confirmDeleteWarning: 'This action cannot be undone.',
    deleteConfirm: 'Delete',
    deleteCancel: 'Cancel',
    deleting: 'Deleting...',
    created: 'Created',
    updated: 'Updated',
    promptCreated: 'Prompt successfully created',
    promptUpdated: 'Prompt successfully updated',
    promptDeleted: 'Prompt successfully deleted',
    error: 'Operation error',
    loadError: 'Error loading prompts',
    saveError: 'Error saving prompt',
    deleteError: 'Error deleting prompt'
  },

  // Changelog
  changelog: {
    title: 'Changelog',
    add: 'Add',
    addEntry: 'Add Entry',
    editEntry: 'Edit Entry',
    component: 'Component',
    version: 'Version',
    date: 'Date',
    dateRelease: 'Release date',
    dateHint: 'You can specify a past release date',
    changes: 'Changes (one per line)',
    changesHint: 'Each line = one change. You can use +, -, *, ✓ at the beginning of the line',
    changesPlaceholder: '- Added new feature\n- Fixed bug with...\n+ Improved performance',
    save: 'Save',
    saving: 'Saving...',
    cancel: 'Cancel',
    confirmDeleteEntry: 'Delete entry {component} v{version}?',
    edit: 'Edit',
    delete: 'Delete',
    noEntries: 'No entries',
    plugin: 'Plugin',
    backend: 'Backend',
    frontend: 'Frontend',
    loading: 'Loading...',
    saved: 'Entry saved',
    deleted: 'Entry deleted',
    error: 'Operation error',
    loadError: 'Failed to load changelog',
    versionRequired: 'Please specify version',
    changesRequired: 'Please add at least one change',
    saveError: 'Error saving changelog',
    deleteError: 'Error deleting entry'
  },

  // Common elements
  common: {
    close: 'Close',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    title: 'Title',
    create: 'Create',
    loading: 'Loading...',
    error: 'Error',
    success: 'Success',
    confirm: 'Confirm',
    search: 'Search',
    yes: 'Yes',
    no: 'No',
    text: 'Text',
    summary: 'Summary',
    download: 'Download',
    downloadFile: 'Download file',
    view: 'View',
    add: 'Add',
    generate: 'Generate',
    clickToCopy: 'Click to copy to clipboard',
    copied: 'Copied to clipboard!',
    copyError: 'Failed to copy',
    languageSwitchIcon: '🇷🇺',
    languageSwitchText: 'Русский',
    connectionError: 'Server connection error'
  },

  // Settings
  settings: {
    title: 'Settings',
    transcription: 'Transcription Settings',
    transcriptionConfig: 'Transcription Settings',
    language: 'Interface Language',
    theme: 'Theme',
    dark: 'Dark theme',
    light: 'Light theme',
    auto: 'Auto',
    temperature: 'Temperature (Transcription Accuracy)',
    temperatureDesc: 'Temperature = 0.0 gives deterministic results and minimizes hallucinations. Increasing the value increases variability, but may decrease accuracy.',
    maxAccuracy: 'Maximum accuracy (recommended)',
    balanced: 'Balanced',
    maxVariability: 'Maximum variability',
    resetToDefaults: 'Reset to defaults',
    settingsSaved: 'Settings saved',
    settingsSaveError: '❌ Error saving settings',
    // Quality mode
    qualityMode: 'Quality Processing',
    qualityModeDesc: 'Use slower but more accurate method with full context instead of fast streaming',
    qualityModeNote: 'Recommended for important meetings and long recordings. Processing will take longer, but quality will be higher.',
    // Save audio
    saveAudio: 'Save audio recording',
    saveAudioDesc: 'Save original audio after transcription. Allows reprocessing in quality mode or downloading.',
    saveAudioNote: 'Audio files take additional server space. Consider deleting unneeded recordings.',
    // Cyclic recording
    autoDeleteOldest: 'Cyclic recording',
    autoDeleteOldestDesc: 'Automatically delete oldest audio recordings when storage limit is exceeded to make room for new ones.',
    autoDeleteOldestNote: 'Locked recordings (🔒) will not be auto-deleted.',
    enabled: 'Enabled',
    disabled: 'Disabled',
    cannotChangeWhileRecording: 'Cannot change setting while recording is active',
    // Storage
    storageTitle: 'Storage Usage',
    storageUsed: 'Used',
    storageLimit: 'Limit',
    storageAudio: 'Audio files',
    storageTranscriptions: 'Transcriptions',
    storageNearLimit: 'Running low on space',
    storageOverLimit: 'Storage limit exceeded',
    storageLimitWarning: 'New audio recordings won\'t be saved when limit is exceeded.',
  },

  // System status
  system: {
    status: 'System Status',
    healthy: 'Healthy',
    degraded: 'Degraded',
    unhealthy: 'Unhealthy',
    unknown: 'Unknown',
    allSystemsHealthy: 'All systems are working normally',
    someSystemsWarning: 'Some systems are running with warnings',
    systemsHaveErrors: 'System problems detected',
    statusUnknown: 'System status unknown',
    services: {
      redis: 'Redis',
      whisper: 'Whisper API',
      openai: 'OpenAI',
      storage: 'Storage'
    }
  },

  // Statistics
  stats: {
    title: 'System Statistics',
    sessions: 'Sessions',
    total: 'Total',
    completed: 'Completed',
    active: 'Active',
    failed: 'Failed',
    processing: 'Processing',
    averageTime: 'Average time',
    totalTime: 'Total time',
    users: 'Users',
    totalUsers: 'Total users',
    activeUsers: 'Active users',
    totalAnonymousCreated: 'Total anonymous sessions created',
    currentAnonymousSessions: 'Current anonymous sessions in system',
    disk: 'Disk',
    free: 'free',
    pluginInstalls: 'Plugin installs',
    pluginInstallsExpanded: 'Plugin Installations',
    anonymousSessions: 'Anonymous sessions',
    allSystemSessions: 'All System Sessions',
    userFilter: 'User:',
    allUsers: 'All users',
    sessionsLabel: 'SESSIONS:',
    completedLabel: 'COMPLETED:',
    errorsLabel: 'ERRORS:',
    durationLabel: 'DURATION:',
    activityPeriod: 'Activity period:',
    last24h: 'Last 24 hours',
    last7d: 'Last 7 days',
    last30d: 'Last 30 days',
    allTime: 'All time',
    version: 'Version',
    id: 'ID',
    email: 'Email',
    lastIp: 'Last IP',
    country: 'Country',
    beta: 'Beta',
    lastActivity: 'Last activity',
    hoursAgo: 'h ago',
    minutesAgo: 'min ago',
    daysAgo: 'd ago',
    hour: 'h',
    minute: 'm',
    day: 'd',
    of: 'of',
    noUserSessions: 'This user has no sessions',
    noSessionsFound: 'No sessions found',
    loadMore: 'Load more',
    loading: 'Loading...',
  },

  // Plugin Errors
  pluginErrors: {
    title: 'Plugin Errors',
    allVersions: 'All versions',
    allTypes: 'All types',
    allTrust: 'All trust levels',
    authenticated: 'Authenticated',
    anonymous: 'Anonymous',
    clearAll: 'Clear all',
    confirmClear: 'Delete all plugin errors? This action is irreversible.',
    time: 'Time',
    version: 'Version',
    type: 'Type',
    message: 'Message',
    trust: 'Trust',
    user: 'User',
    count: 'Count',
    noErrors: 'No errors found',
  },

  // Modals
  modal: {
    close: 'Close',
    confirm: 'Confirm',
    installPlugin: {
      title: 'AI Stenograph Install Plugin',
      chromeWebStore: 'Install from Chrome Web Store',
      chromeWebStoreDesc: 'The easiest way to install is through the official Chrome extension store. Click the button below to go to the plugin page.',
      openChromeStore: 'Open in Chrome Web Store'
    },
    tips: {
      title: 'Tips & Tricks',
      authTitle: 'Sign In',
      authDesc: 'Sign in with the same Google account you use on the website to save your transcripts to the server.',
      authAnonymous: 'You can also use anonymous mode — your recordings will be stored locally only.',
      webViewTitle: 'Web Viewer',
      webViewDesc: 'Click the cloud icon in the recordings list to open a transcript in the web viewer.',
      editTitle: 'Editing Recordings',
      editDesc: 'You can rename your recordings or delete them — both locally and from the server.',
      hotkeysTitle: 'Hotkeys',
      hotkeyRecord: '— start or stop recording',
      hotkeyActivation: '— enable or disable the plugin',
      hotkeyMic: '— mute or unmute the microphone',
      permissionsTitle: 'First-Time Setup',
      permissionsDesc: 'On first use, grant microphone access and tab audio recording permissions.',
      autoStopTitle: 'Auto Stop',
      autoStopDesc: 'Configure automatic stopping when no sound is detected. The default is 1 minute.',
      googleMeetTitle: 'Google Meet',
      googleMeetDesc: 'The plugin can automatically start recording when you join a Google Meet call.',
      micDetectionTitle: 'Microphone State Detection',
      micDetectionDesc: 'The plugin detects the microphone status in Google Meet: if you mute it in the meeting, it will not be recorded.',
      advancedSettingsTitle: 'Advanced Settings',
      advancedSettingsDesc: 'Tap the plugin version at the bottom of the settings page to access advanced options.',
      audioSourceTitle: 'Audio Source',
      audioSourceDesc: 'Choose what to record: microphone only or tab audio only.',
      languageTitle: 'Meeting Language',
      languageDesc: 'Set the meeting language beforehand or during recording to improve recognition. If the language is set incorrectly, the transcript will be translated into that language.'
    },
    privacy: {
      title: 'Privacy Policy',
      loadError: 'Failed to load privacy policy'
    }
  },
}

